import React from 'react';
import useStyles from './styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import { Avatar, ThemeToggle, User } from '@elevator/ui';
import UserBadge from 'atoms/UserBadge';
import { BaseLink } from '@elevator/ui';
import { ROUTES } from 'config/Nav';
import * as IconMUI from '@mui/icons-material';
import * as IconMDI from 'mdi-material-ui';
import clsx from 'clsx';

export interface AccountMenuProps {
  currentUser: User | null;
  onClose: () => void;
  handleSignoutClick: () => void;
  handleSubmitClick: () => void;
}

const ThemeToggleWrapper = ({ className }: { className: string }) => (
  <Box onClick={(e) => e.stopPropagation()} className={className}>
    <ThemeToggle size="small" color="inherit" />
  </Box>
);

const interpolateBadgeType = (user: User) => {
  if (user.permissionGroup?.groupName !== 'Standard') {
    return <UserBadge variant="staff" />;
  }
  if (user.preferred) {
    return <UserBadge variant="preferred" />;
  }
  if (user.verified) {
    return <UserBadge variant="verified" />;
  }
  return null;
};

const AccountMenu = ({
  currentUser,
  onClose,
  handleSignoutClick,
  handleSubmitClick,
}: AccountMenuProps) => {
  const { classes } = useStyles();

  if (!currentUser) {
    return (
      <Paper elevation={2} className={classes.root} data-component="AccountMenu">
        <List className={classes.userOptionsList}>
          <ListItem
            className={clsx(classes.lastItem, classes.loginButtonRow)}
            disablePadding
          >
            <ListItemButton
              className={classes.listItem}
              component={BaseLink as any}
              href={ROUTES.AUTH.LOG_IN.to || '#'}
              underline="none"
              color="inherit"
              onClick={onClose}
              sx={{ flex: 1, padding: 0 }}
            >
              <Box className={classes.profileContent}>
                <ListItemIcon className={classes.listIcon}>
                  <IconMUI.Login />
                </ListItemIcon>
                <ListItemText
                  primary="Log In"
                  slotProps={{ primary: { className: classes.listItem } }}
                />
              </Box>
            </ListItemButton>
            <ThemeToggleWrapper className={classes.themeToggleHidden} />
          </ListItem>
        </List>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} className={classes.root} data-component="AccountMenu">
      <Paper elevation={10} className={classes.profileListItem}>
        <Box className={classes.profileContent}>
          <ListItemAvatar>
            <Badge badgeContent={interpolateBadgeType(currentUser)}>
              <Avatar
                username={currentUser.firstName}
                size="small"
                src={currentUser.avatar?.sourceUrl}
                alt={`${currentUser.preferredUsername} avatar`}
              />
            </Badge>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="overline1" className={classes.profileName}>
                {currentUser.preferredUsername}
              </Typography>
            }
            secondary={
              <Typography variant="body2" className={classes.emailText}>
                {currentUser.email}
              </Typography>
            }
          />
        </Box>
        <ThemeToggleWrapper className={classes.themeToggleHidden} />
      </Paper>

      <List className={classes.userOptionsList}>
        <ListItem
          className={classes.listItem}
          component={BaseLink as any}
          href={`${ROUTES.USER.to}${currentUser.preferredUsername}`}
          underline="none"
          color="inherit"
          onClick={onClose}
        >
          <ListItemIcon className={classes.listIcon}>
            <IconMUI.Person />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItem>
        <ListItem
          className={classes.listItem}
          component={BaseLink as any}
          href={ROUTES.ACCOUNT.SUBMISSIONS.to}
          underline="none"
          color="inherit"
          onClick={onClose}
        >
          <ListItemIcon className={classes.listIcon}>
            <IconMDI.Inbox />
          </ListItemIcon>
          <ListItemText
            primary="Submissions"
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItem>
        <ListItemButton
          className={classes.listItem}
          onClick={() => {
            onClose();
            handleSubmitClick();
          }}
        >
          <ListItemIcon className={classes.listIcon}>
            <IconMDI.InboxArrowDown />
          </ListItemIcon>
          <ListItemText
            primary="Submit"
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItemButton>
        <Divider component="li" className={classes.listDivider} />
        {currentUser.permissionGroup?.groupName !== 'Standard' && (
          <ListItem
            component={BaseLink as any}
            href={ROUTES.ADMIN?.to ?? '#'}
            underline="none"
            color="inherit"
            className={clsx(classes.listItem, classes.listItemHighEmphasis)}
            onClick={onClose}
          >
            <ListItemIcon className={classes.listIcon}>
              <IconMDI.ViewDashboard />
            </ListItemIcon>
            <ListItemText
              primary="Overview"
              slotProps={{ primary: { className: classes.listItemHighEmphasis } }}
            />
          </ListItem>
        )}
        <ListItem
          className={classes.listItem}
          component={BaseLink as any}
          href={ROUTES.ACCOUNT.to}
          underline="none"
          color="inherit"
          onClick={onClose}
        >
          <ListItemIcon className={classes.listIcon}>
            <IconMDI.Cog />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItem>
        <ListItem
          className={classes.listItem}
          component={BaseLink as any}
          href={ROUTES.HELP.to}
          underline="none"
          color="inherit"
          onClick={onClose}
        >
          <ListItemIcon className={classes.listIcon}>
            <IconMUI.Help />
          </ListItemIcon>
          <ListItemText
            primary="Help"
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItem>

        <Divider component="li" className={classes.listDivider} />

        <ListItemButton
          className={clsx(classes.listItem, classes.lastItem)}
          onClick={() => {
            onClose();
            handleSignoutClick();
          }}
        >
          <ListItemIcon className={classes.listIcon}>
            <IconMUI.Logout />
          </ListItemIcon>
          <ListItemText
            primary="Log Out"
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItemButton>
      </List>
    </Paper>
  );
};

export default AccountMenu;
